import { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatHour = (h) => {
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display} ${suffix}`;
};

/**
 * Live badge showing remaining bookable slots for TODAY.
 * Props:
 *  - serviceType: 'digimitra' | 'youtuber'
 *  - accent: 'dm' | 'yt' (affects color)
 *  - testId: data-testid prefix
 */
const SlotsTodayBadge = ({ serviceType, accent = 'dm', testId = 'slots-today' }) => {
  const [slots, setSlots] = useState(null);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/appointments/availability`, {
          params: { date: today, service_type: serviceType },
        });
        // Filter to slots that are still in the future today
        const now = new Date();
        const currentHour = now.getHours();
        const future = (res.data.slots || []).filter((s) => s.hour > currentHour && s.available);
        if (!cancelled) setSlots(future);
      } catch {
        if (!cancelled) setSlots([]);
      }
    })();
    return () => { cancelled = true; };
  }, [serviceType]);

  if (slots === null) return null; // still loading — render nothing
  if (slots.length === 0) return null; // nothing left today — stay quiet

  const remaining = slots.length;
  const nextSlot = slots[0];
  const isUrgent = remaining <= 3;

  return (
    <div
      className={`slots-today-badge slots-today-${accent} ${isUrgent ? 'urgent' : ''}`}
      data-testid={testId}
    >
      <span className="slots-today-dot" />
      <Zap size={14} className="slots-today-icon" />
      <span className="slots-today-text">
        <strong data-testid={`${testId}-count`}>{remaining} slot{remaining === 1 ? '' : 's'} left today</strong>
        <span className="slots-today-sub"><Clock size={11} /> next at {formatHour(nextSlot.hour)}</span>
      </span>
    </div>
  );
};

export default SlotsTodayBadge;
